import React, { useState } from "react";
import "./CreateContent.css";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
  Alert,
} from "@mui/material";

export default function CreateContent() {
  const [type, setType] = useState("");

  const [postFormData, setPostFormData] = useState({
    title: "",
    subtitle: "",
    content: "",
    images: [],
    pdfs: [],
    category: "",
  });

  const [videoFormData, setVideoFormData] = useState({
    title: "",
    subtitle: "",
    videoUrl: "",
    instructor: "",
  });

  const [alert, setAlert] = useState({ message: "", severity: "" });

  const showAlert = (message, severity = "info") => {
    setAlert({ message, severity });
    setTimeout(() => setAlert({ message: "", severity: "" }), 4000);
  };

  const handleOption = (event) => {
    setType(event.target.value);
  };

  const handlePostChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "images") {
      setPostFormData((prev) => ({
        ...prev,
        images: Array.from(files),
      }));
    } else if (name === "pdfs") {
      setPostFormData((prev) => ({
        ...prev,
        pdfs: Array.from(files),
      }));
    } else {
      setPostFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleVideoChange = (e) => {
    const { name, value } = e.target;
    setVideoFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.userId || !user.name) {
      showAlert("User info is missing.", "error");
      return;
    }

    if (type === "post") {
      const { title, subtitle, content, category, images } = postFormData;

      if (!title || !subtitle || !category) {
        showAlert("Please fill all required post fields.", "warning");
        return;
      }

      const submissionData = new FormData();
      submissionData.append("title", title);
      submissionData.append("subtitle", subtitle);
      submissionData.append("content", content);
      submissionData.append("category", category);
      submissionData.append("userId", user.userId);
      submissionData.append("username", user.name);

      for (let i = 0; i < images.length; i++) {
        submissionData.append("images", images[i]);
      }
      for (let i = 0; i < postFormData.pdfs.length; i++) {
        submissionData.append("pdfs", postFormData.pdfs[i]);
      }

      // Log all FormData entries before sending
      for (let pair of submissionData.entries()) {
        console.log(pair[0], pair[1]);
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/pendingPosts`,
          {
            method: "POST",
            body: submissionData,
          }
        );

        const result = await response.json();
        if (response.ok) {
          showAlert("Post submitted for review!", "success");
          setPostFormData({
            title: "",
            subtitle: "",
            content: "",
            images: [],
            category: "",
          });
          setType("");
        } else {
          showAlert(result.error || "Failed to submit post.", "error");
        }
      } catch (err) {
        console.error("Submission error:", err);
        showAlert("Something went wrong while submitting.", "error");
      }
    } else {
      const { videoUrl, title, subtitle, instructor } = videoFormData;

      if (!videoUrl || !title || !subtitle || !instructor) {
        showAlert("Please fill all fields for the video.", "warning");
        return;
      }

      const youtubeRegex = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
      if (!youtubeRegex.test(videoUrl)) {
        showAlert("Please enter a valid YouTube URL.", "warning");
        return;
      }

      const submissionData = {
        title,
        subtitle,
        videoUrl,
        instructor,
        userId: user.userId,
        username: user.name,
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/pendingVideos`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(submissionData),
          }
        );

        const result = await response.json();
        if (response.ok) {
          showAlert("Video submitted for review!", "success");
          setVideoFormData({
            title: "",
            subtitle: "",
            videoUrl: "",
            instructor: "",
          });
          setType("");
        } else {
          showAlert(result.error || "Failed to submit video.", "error");
        }
      } catch (err) {
        console.error("Submission error:", err);
        showAlert("Something went wrong while submitting.", "error");
      }
    }
  };

  return (
    <div className="create-content-container">
      <h2>Create Content</h2>

      {alert.message && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ message: "", severity: "" })}
          sx={{ my: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <FormControl fullWidth>
        <InputLabel id="demo-simple-select-label">Post Type</InputLabel>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={type}
          label="Type"
          onChange={handleOption}
        >
          <MenuItem value="post">Post</MenuItem>
          <MenuItem value="youtube">YouTube Video</MenuItem>
        </Select>
      </FormControl>

      <Box mt={5} />

      <form className="content-form" onSubmit={handleSubmit}>
        {type === "" ? (
          ""
        ) : type === "post" ? (
          <>
            <FormControl fullWidth style={{ marginTop: "1rem" }}>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                name="category"
                value={postFormData.category}
                onChange={handlePostChange}
                required
              >
                <MenuItem value="Announcements">Announcements</MenuItem>
                <MenuItem value="News/Events">News/Events</MenuItem>
                <MenuItem value="Admissions">Admissions</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            <label>
              Title
              <input
                type="text"
                name="title"
                value={postFormData.title}
                onChange={handlePostChange}
                required
              />
            </label>

            <label>
              Subtitle
              <input
                type="text"
                name="subtitle"
                value={postFormData.subtitle}
                onChange={handlePostChange}
                required
              />
            </label>

            <label>
              Content
              <textarea
                name="content"
                rows="5"
                value={postFormData.content}
                onChange={handlePostChange}
              />
            </label>

            <Box mt={3}>
              <InputLabel>Upload Images</InputLabel>
              <br />
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  style={{ textTransform: "none" }}
                >
                  Choose Images
                  <input
                    type="file"
                    name="images"
                    multiple
                    accept="image/*"
                    hidden
                    onChange={handlePostChange}
                  />
                </Button>
                {postFormData.images.length > 0 && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      setPostFormData((prev) => ({ ...prev, images: [] }))
                    }
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {postFormData.images.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={2}>
                  {postFormData.images.map((file, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 1,
                        border: "1px solid #ddd",
                      }}
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`preview-${i}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <InputLabel sx={{ mt: 2 }}>Upload PDFs</InputLabel>
              <br />
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  style={{ textTransform: "none" }}
                >
                  Choose PDFs
                  <input
                    type="file"
                    name="pdfs"
                    multiple
                    accept="application/pdf"
                    hidden
                    onChange={handlePostChange}
                  />
                </Button>
                {postFormData.pdfs.length > 0 && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      setPostFormData((prev) => ({ ...prev, pdfs: [] }))
                    }
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {postFormData.pdfs.length > 0 && (
                <Box display="flex" flexDirection="column" gap={1}>
                  {postFormData.pdfs.map((file, i) => (
                    <Box key={i}>
                      <span>{file.name}</span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </>
        ) : (
          <>
            <label>
              YouTube Video URL
              <input
                type="url"
                name="videoUrl"
                value={videoFormData.videoUrl}
                onChange={handleVideoChange}
                required
              />
            </label>

            <label>
              Title
              <input
                type="text"
                name="title"
                value={videoFormData.title}
                onChange={handleVideoChange}
                required
              />
            </label>

            <label>
              Subtitle
              <input
                type="text"
                name="subtitle"
                value={videoFormData.subtitle}
                onChange={handleVideoChange}
                required
              />
            </label>

            <label>
              Instructor
              <input
                type="text"
                name="instructor"
                value={videoFormData.instructor}
                onChange={handleVideoChange}
                required
              />
            </label>
          </>
        )}

        <button type="submit">Submit</button>
      </form>
    </div>
  );
}
